import { useEffect, useState } from 'react'
import { useUserCountry } from './useUserCountry'

// --- New, more detailed price structures ---

// Represents the price details for a single plan (monthly or annual)
export interface PriceInfo {
  priceId: string
  amount: number // in cents, e.g., 999 for $9.99
}

// Contains the details for both monthly and annual plans for a single currency
export interface PlanDetails {
  monthly: PriceInfo
  annual: PriceInfo
  currency: 'USD' | 'BRL'
}

// This is the expected structure from the PostHog remote config payload
interface AllPlans {
  BRL?: Omit<PlanDetails, 'currency'>
  USD?: Omit<PlanDetails, 'currency'>
}

// Function to wait for PostHog to be ready
const waitForPostHog = async (maxWaitMs = 5000): Promise<typeof window.posthog | null> => {
  const startTime = Date.now()

  while (Date.now() - startTime < maxWaitMs) {
    if (window.posthog && typeof window.posthog.getFeatureFlagPayload === 'function') {
      return window.posthog
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  console.error('PostHog timeout after', maxWaitMs, 'ms')
  return null
}

// --- Generic PostHog hook (updated to use window.posthog directly) ---
export function usePostHogRemoteConfig<T = unknown>(
  key: string
): {
  data: T | null
  isLoading: boolean
  error: string | null
} {
  const [data, setData] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchRemoteConfig = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Wait for PostHog to be ready
        const posthog = await waitForPostHog()
        if (!posthog) {
          throw new Error('PostHog failed to initialize within timeout period')
        }

        const remoteConfigPayload = await posthog.getFeatureFlagPayload(key)
        if (!remoteConfigPayload) {
          throw new Error(`Remote config for key '${key}' not found or empty.`)
        }
        setData(remoteConfigPayload as T)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch remote config'
        setError(errorMessage)
        console.error(`PostHog Remote Config Error for key '${key}':`, errorMessage)
      } finally {
        setIsLoading(false)
      }
    }
    fetchRemoteConfig()
  }, [key])

  return { data, isLoading, error }
}

// --- Updated hook for Stripe plans with country logic and fallbacks ---
export function useStripePlans() {
  const { country, isLoading: isLoadingCountry, error: countryError } = useUserCountry()

  const [plans, setPlans] = useState<PlanDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const getPlans = async () => {
      if (isLoadingCountry) {
        console.log('Still loading country, waiting...')
        return
      }

      console.log('Starting to get plans for country:', country)
      setIsLoading(true)
      setError(countryError)

      try {
        console.log('Waiting for PostHog to be ready...')
        // Wait for PostHog to be ready
        const posthog = await waitForPostHog()
        console.log('PostHog ready:', !!posthog)

        if (posthog) {
          console.log('Fetching feature flag payload for stripe_price_ids...')
          const allPlans = (await posthog.getFeatureFlagPayload('stripe_price_ids')) as AllPlans
          console.log('Received plans from PostHog:', allPlans)

          let determinedPlans: PlanDetails | undefined

          if (country === 'BR' && allPlans?.BRL) {
            console.log('Using BRL plans for Brazil')
            determinedPlans = { ...allPlans.BRL, currency: 'BRL' }
          } else if (allPlans?.USD) {
            console.log('Using USD plans')
            determinedPlans = { ...allPlans.USD, currency: 'USD' }
          }

          if (determinedPlans) {
            console.log('Setting determined plans:', determinedPlans)
            setPlans(determinedPlans)
            return
          }
        }
        throw new Error('Could not retrieve plans from PostHog. Attempting fallback.')
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar preços'
        console.error('Failed to load pricing:', errorMessage)
        setError(
          'Não foi possível carregar os preços no momento. Tente novamente em alguns instantes.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    getPlans()
  }, [country, isLoadingCountry, countryError])

  return { data: plans, isLoading: isLoading || isLoadingCountry, error }
}
